import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function SettingsPage() {
  const [tenant, setTenant] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings/tenant");
    setTenant(data);
  };
  useEffect(() => { load(); }, []);

  if (!tenant) return <div className="label-overline text-slate-500">Loading…</div>;

  const patch = (path, val) => {
    setTenant((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = val;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        business_name: tenant.business_name,
        trading_name: tenant.trading_name,
        vat_registered: tenant.vat_registered,
        vat_number: tenant.vat_number,
        vat_rate_bp: tenant.vat_rate_bp,
        address: tenant.address,
        contact: tenant.contact,
        banking_details: tenant.banking_details,
        document_notes: tenant.document_notes,
        receipt_config: tenant.receipt_config,
        // Only send the whitelisted, tenant-controllable flag(s). The backend
        // ignores any other keys — this is defence in depth.
        feature_flags: {
          auto_invoice_on_complete:
            !!tenant.feature_flags?.auto_invoice_on_complete,
        },
      };
      await api.patch("/settings/tenant", body);
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <div className="label-overline">Account</div>
        <h1 className="text-3xl font-black tracking-tight mt-1">Settings</h1>
      </div>

      <div className="space-y-6">
        <section className="bg-white border border-slate-200 p-5">
          <div className="label-overline mb-3">Business</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-overline">Business name</Label>
              <Input data-testid="set-business" className="rounded h-10 mt-1"
                     value={tenant.business_name || ""}
                     onChange={(e) => patch("business_name", e.target.value)} />
            </div>
            <div>
              <Label className="label-overline">Trading name</Label>
              <Input data-testid="set-trading" className="rounded h-10 mt-1"
                     value={tenant.trading_name || ""}
                     onChange={(e) => patch("trading_name", e.target.value)} />
            </div>
          </div>
        </section>

        <section className="bg-white border border-slate-200 p-5">
          <div className="label-overline mb-3">Address</div>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Line 1" className="rounded h-10" data-testid="set-addr-1"
                   value={tenant.address?.line1 || ""} onChange={(e) => patch("address.line1", e.target.value)} />
            <Input placeholder="Line 2" className="rounded h-10" data-testid="set-addr-2"
                   value={tenant.address?.line2 || ""} onChange={(e) => patch("address.line2", e.target.value)} />
            <Input placeholder="City" className="rounded h-10" data-testid="set-addr-city"
                   value={tenant.address?.city || ""} onChange={(e) => patch("address.city", e.target.value)} />
            <Input placeholder="Province" className="rounded h-10" data-testid="set-addr-prov"
                   value={tenant.address?.province || ""} onChange={(e) => patch("address.province", e.target.value)} />
            <Input placeholder="Postal code" className="rounded h-10 font-mono" data-testid="set-addr-pc"
                   value={tenant.address?.postal_code || ""} onChange={(e) => patch("address.postal_code", e.target.value)} />
          </div>
        </section>

        <section className="bg-white border border-slate-200 p-5">
          <div className="label-overline mb-3">Contact</div>
          <div className="grid grid-cols-3 gap-3">
            <Input placeholder="Phone" className="rounded h-10 font-mono" data-testid="set-phone"
                   value={tenant.contact?.phone || ""} onChange={(e) => patch("contact.phone", e.target.value)} />
            <Input placeholder="Email" className="rounded h-10 font-mono" data-testid="set-email"
                   value={tenant.contact?.email || ""} onChange={(e) => patch("contact.email", e.target.value)} />
            <Input placeholder="Website" className="rounded h-10 font-mono" data-testid="set-website"
                   value={tenant.contact?.website || ""} onChange={(e) => patch("contact.website", e.target.value)} />
          </div>
        </section>

        <section className="bg-white border border-slate-200 p-5">
          <div className="label-overline mb-3">Receipt (Operational Driver Slip)</div>
          <p className="text-xs text-slate-600 mb-3">
            The Stage 1 receipt is an operational driver slip, marked
            <b className="text-red-600"> Not a Tax Invoice</b>. VAT tax invoices are Stage 2.
          </p>
          <Label className="label-overline">Receipt footer</Label>
          <Textarea data-testid="set-receipt-footer" rows={2} className="rounded mt-1"
                    value={tenant.receipt_config?.footer || ""}
                    onChange={(e) => patch("receipt_config.footer", e.target.value)} />
        </section>

        {tenant.plan === "financial" && tenant.feature_flags?.financial && (
          <section className="bg-white border border-slate-200 p-5"
                   data-testid="finance-settings-card">
            <div className="label-overline mb-3">Finance Settings</div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox"
                     data-testid="set-auto-invoice-toggle"
                     className="mt-1 h-4 w-4 rounded border-slate-300"
                     checked={!!tenant.feature_flags?.auto_invoice_on_complete}
                     onChange={(e) => patch("feature_flags.auto_invoice_on_complete",
                                            e.target.checked)} />
              <div className="text-sm">
                <div className="font-bold text-slate-900">
                  Auto-invoice completed customer orders
                </div>
                <div className="text-slate-600 text-xs mt-1">
                  When <b>ON</b>, completed customer orders with a resolved rate are
                  immediately invoiced and posted to the customer's wallet.
                  When <b>OFF</b>, completed orders go to <b>Uninvoiced Charges</b>{" "}
                  for manual review and batch invoicing.
                </div>
                <div className="text-slate-500 text-[11px] mt-2">
                  Walk-in orders, archived orders and orders without a resolved rate
                  never auto-invoice — those always go to Uninvoiced Charges for
                  review. Only owner/admin can change this.
                </div>
              </div>
            </label>
          </section>
        )}

        <div className="flex justify-end">
          <Button data-testid="set-save" onClick={save} disabled={saving}
                  className="rounded bg-blue-700 hover:bg-blue-700 h-11 px-6">
            {saving ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </div>
    </div>
  );
}
